<template>
<div>
    <h1>Planning</h1>
    <DialogView v-model:visible="planningEditVisible" style="width:70%; text-align:left">
        <template #header>
            <h3>{{ planningDialogMode === 'create' ? 'Nouvelle période de planning' : 'Modifier période de planning' }}</h3>
        </template>
        <div class="planningEditForm">
            <div class="field">
                <label for="editBegin">Début période</label>
                <CalendarView id="editBegin" v-model="editingBeginDate" :manualInput="false" />
            </div>
            <div class="field">
                <label for="editEnd">Fin période (optionnel)</label>
                <CalendarView id="editEnd" v-model="editingEndDate" :manualInput="false" />
            </div>
        </div>

        <div class="planningEditRecurrences">
            <h4>Récurrences modifiables</h4>
            <div class="planningEditRow planningEditRowHeader">
                <div>Jour</div>
                <div>A ouvert</div>
                <div>A début</div>
                <div>A fin</div>
                <div>B ouvert</div>
                <div>B début</div>
                <div>B fin</div>
            </div>
            <div v-for="item in dayScheduleRows" :key="item.dayOfWeek" class="planningEditRow">
                <div class="planningEditDayLabel">{{ dayLabel(item.dayOfWeek) }}</div>
                <CheckBox v-model="item.weekA.enabled" :binary="true" />
                <InputText v-model="item.weekA.startTime" placeholder="HH:mm" :disabled="!item.weekA.enabled" />
                <InputText v-model="item.weekA.endTime" placeholder="HH:mm" :disabled="!item.weekA.enabled" />
                <CheckBox v-model="item.weekB.enabled" :binary="true" />
                <InputText v-model="item.weekB.startTime" placeholder="HH:mm" :disabled="!item.weekB.enabled" />
                <InputText v-model="item.weekB.endTime" placeholder="HH:mm" :disabled="!item.weekB.enabled" />
            </div>
        </div>

        <div v-if="readonlyRecurrences.length > 0" class="planningReadonly">
            <h4>Ouvertures ponctuelles (lecture seule)</h4>
            <div v-for="item in readonlyRecurrences" :key="'ro-' + item.index">
                {{ item.date }} {{ item.startTime }}-{{ item.endTime }}
            </div>
        </div>
        <template #footer>
            <InputButton icon="pi pi-save" :label="planningDialogMode === 'create' ? 'Créer période' : 'Sauvegarder période'" class="p-button-outlined p-button-secondary" @click="savePlanningPeriod" :disabled="planningUpdating"/>
            <InputButton icon="pi pi-times" label="Annuler" class="p-button-text" @click="planningEditVisible=false"/>
        </template>
    </DialogView>

    <div class="planningTabs">
        <InputButton label="Périodes" class="p-button-sm" :class="tabButtonClass('periods')" @click="activeTab = 'periods'" />
        <InputButton label="Jours d'exception" class="p-button-sm" :class="tabButtonClass('exceptions')" @click="activeTab = 'exceptions'" />
    </div>

    <!-- Planning periods tab -->
    <CardView class="planningCard" v-if="activeTab === 'periods'">
        <template #header>
            <h2>Périodes de planning</h2>
        </template>
        <template #content>
            <GridLoader v-if="planningLoading" class="loader"></GridLoader>
            <div v-if="!planningLoading && planningPeriods.length === 0">
                Aucune période de planning.
            </div>
            <div v-if="!planningLoading && planningPeriods.length > 0" class="planningPeriodsList">
                <div class="planningPeriodCard" v-for="period in planningPeriods" :key="period.planningId">
                    <div class="planningPeriodTop">
                        <div class="planningRange">
                            <div class="rangeItem">
                                <span class="rangeLabel">Début</span>
                                <strong>{{ period.begin }}</strong>
                            </div>
                            <div class="rangeItem">
                                <span class="rangeLabel">Fin</span>
                                <strong>{{ period.end ? period.end : 'Actif' }}</strong>
                            </div>
                        </div>
                        <div class="planningActions">
                            <InputButton v-if="period.editable === 'true'" icon="pi pi-pencil" class="p-button-text p-button-sm" @click="openPlanningEdit(period)"/>
                            <InputButton v-if="period.editable === 'true'" icon="pi pi-trash" class="p-button-text p-button-sm p-button-danger" @click="deletePlanningPeriod(period)" :disabled="planningDeletingId === period.planningId"/>
                            <span v-else class="readonlyBadge">Verrouillé</span>
                        </div>
                    </div>
                    <div class="planningRecurrences">
                        <div class="planningWeekBlock">
                            <div class="planningWeekTitle">Semaine A</div>
                            <div class="planningRecurrenceRow" v-for="row in buildWeekRowsForWeek(period, 'A')" :key="`${period.planningId}-A-${row.day}`">
                                <span class="recurrenceDay">{{ row.label }}</span>
                                <span class="recurrenceValue">{{ row.value }}</span>
                            </div>
                        </div>
                        <div class="planningWeekBlock">
                            <div class="planningWeekTitle">Semaine B</div>
                            <div class="planningRecurrenceRow" v-for="row in buildWeekRowsForWeek(period, 'B')" :key="`${period.planningId}-B-${row.day}`">
                                <span class="recurrenceDay">{{ row.label }}</span>
                                <span class="recurrenceValue">{{ row.value }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>
        <template #footer>
            <InputButton icon="pi pi-plus" label="Nouvelle période" class="p-button-outlined p-button-secondary planningNewButton" @click="openNewPlanning"/>
            <InputButton icon="pi pi-refresh" label="Rafraîchir" class="p-button-outlined p-button-secondary" @click="loadPlanningPeriods"/>
        </template>
    </CardView>

    <!-- Exception days tab -->
    <CardView class="planningCard" v-if="activeTab === 'exceptions'">
        <template #header>
            <h2>Jours d'exception</h2>
            <p class="exceptionSubtitle">Jours fériés, vacances ou congés qui remplacent le planning actif. Aucun rendez-vous ne sera possible ces jours-là.</p>
        </template>
        <template #content>
            <GridLoader v-if="exceptionsLoading" class="loader"></GridLoader>

            <div class="exceptionAddForm">
                <div class="field">
                    <label for="newExDate">Du</label>
                    <CalendarView id="newExDate" v-model="newExceptionDate" :manualInput="false" />
                </div>
                <div class="field">
                    <label for="newExDateEnd">Au (optionnel)</label>
                    <CalendarView id="newExDateEnd" v-model="newExceptionEndDate" :manualInput="false" />
                </div>
                <div class="field">
                    <label for="newExLabel">Libellé (optionnel)</label>
                    <InputText id="newExLabel" v-model="newExceptionLabel" placeholder="ex: Noël, Vacances..." />
                </div>
                <InputButton icon="pi pi-plus" label="Ajouter" class="p-button-outlined p-button-secondary exceptionAddBtn" @click="addException" :disabled="exceptionsLoading" />
            </div>

            <div v-if="!exceptionsLoading && exceptions.length === 0" class="exceptionEmpty">
                Aucun jour d'exception.
            </div>

            <div v-if="!exceptionsLoading && exceptions.length > 0" class="exceptionList">
                <div class="exceptionItem" v-for="ex in exceptions" :key="ex.planningExceptionId">
                    <div class="exceptionInfo">
                        <strong>{{ formatExceptionDate(ex.exceptionDate) }}</strong>
                        <span v-if="ex.label" class="exceptionLabel">{{ ex.label }}</span>
                    </div>
                    <div class="exceptionActions">
                        <InputButton icon="pi pi-trash" class="p-button-text p-button-sm p-button-danger" @click="deleteException(ex)" />
                    </div>
                </div>
            </div>
        </template>
        <template #footer>
            <InputButton icon="pi pi-refresh" label="Rafraîchir" class="p-button-outlined p-button-secondary" @click="loadExceptions"/>
        </template>
    </CardView>

</div>
</template>

<style scoped>
   .planningTabs {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        margin: 10px 0 12px 0;
   }

   .planningTabButton {
        border-color: #ced9ec !important;
        color: #425574 !important;
        background: #ffffff !important;
   }

   .planningTabButtonActive {
        border-color: #8eadde !important;
        background: #edf4ff !important;
        color: #2c4569 !important;
        font-weight: 700;
   }

   .planningCard {
        margin-bottom: 14px;
   }

   .planningCard :deep(.p-card-header),
   .planningCard :deep(.p-card-content),
   .planningCard :deep(.p-card-footer) {
        padding-left: 14px;
        padding-right: 14px;
   }

   .planningCard :deep(.p-card-content) {
        padding-top: 10px;
        padding-bottom: 12px;
   }

   .planningNewButton {
        margin-right: 8px;
   }

   .planningEditForm {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 10px 16px;
   }

   .planningEditRecurrences {
        margin-top: 12px;
   }

   .planningEditRow {
        display: grid;
        grid-template-columns: 120px 72px 100px 100px 72px 100px 100px;
        gap: 10px;
        margin-bottom: 8px;
        align-items: center;
   }

   .planningEditRowHeader {
        font-size: 0.8rem;
        text-transform: uppercase;
        color: #60718f;
        font-weight: 700;
        margin-bottom: 10px;
   }

   .planningEditDayLabel {
        display: flex;
        align-items: center;
        font-weight: bold;
   }



   .planningReadonly {
        margin-top: 14px;
        border-top: 1px solid #e0e8f4;
        padding-top: 12px;
   }

   .planningPeriodsList {
        display: grid;
        gap: 8px;
   }

   .planningPeriodCard {
        border: 1px solid #e2e9f5;
        border-radius: 10px;
        background: #ffffff;
        padding: 12px 12px 8px 12px;
   }

   .planningPeriodTop {
        display: flex;
        justify-content: space-between;
        gap: 10px;
        align-items: center;
        margin-bottom: 10px;
   }

   .planningRange {
        display: flex;
        flex-wrap: wrap;
        gap: 12px;
   }

   .rangeItem {
        display: flex;
        flex-direction: column;
        min-width: 140px;
   }

   .rangeLabel {
        font-size: 0.75rem;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: #60718f;
   }

   .planningActions {
        display: flex;
        align-items: center;
        gap: 8px;
   }

   .readonlyBadge {
        border: 1px solid #d4ddeb;
        background: #eef3fb;
        color: #637593;
        border-radius: 999px;
        padding: 4px 10px;
        font-size: 0.78rem;
        font-weight: 600;
   }

   .planningRecurrences {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 8px 12px;
   }

   .planningWeekBlock {
        border: 1px solid #e6edf8;
        border-radius: 8px;
        padding: 8px 10px;
        background: #fbfdff;
   }

   .planningWeekTitle {
        font-size: 0.78rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.03em;
        color: #5f7190;
        margin-bottom: 6px;
   }

   .planningRecurrenceRow {
        display: flex;
        justify-content: space-between;
        gap: 10px;
        padding: 7px 0;
        border-bottom: 1px dashed #dfe7f4;
   }

   .planningRecurrenceRow:last-child {
        border-bottom: 0;
   }

   .recurrenceDay {
        font-weight: 600;
        color: #2c3f61;
   }

   .recurrenceValue {
        color: #4f5f7d;
   }

   /* Exception days styles */
   .exceptionSubtitle {
        margin: 0;
        font-size: 0.88rem;
        color: #5b6a83;
   }

   .exceptionAddForm {
        display: flex;
        flex-wrap: wrap;
        align-items: flex-end;
        gap: 12px;
        margin-bottom: 16px;
        padding-bottom: 14px;
        border-bottom: 1px solid #e0e8f4;
   }

   .exceptionAddForm .field {
        display: flex;
        flex-direction: column;
        gap: 4px;
   }

   .exceptionAddForm label {
        font-size: 0.82rem;
        color: #60718f;
   }

   .exceptionAddBtn {
        margin-bottom: 2px;
   }

   .exceptionEmpty {
        color: #7a8ba5;
        padding: 8px 0;
   }

   .exceptionList {
        display: grid;
        gap: 6px;
   }

   .exceptionItem {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 10px 12px;
        border: 1px solid #e2e9f5;
        border-radius: 8px;
        background: #ffffff;
   }

   .exceptionInfo {
        display: flex;
        align-items: center;
        gap: 12px;
   }

   .exceptionLabel {
        color: #5b6a83;
        font-size: 0.9rem;
   }

   @media (max-width: 900px) {
        .planningTabs {
            gap: 6px;
        }

        .planningEditForm {
            grid-template-columns: 1fr;
        }

        .planningEditRow {
            grid-template-columns: 1fr;
            gap: 6px;
            padding: 6px 0;
        }

        .planningEditRowHeader {
            display: none;
        }

        .planningRecurrences {
            grid-template-columns: 1fr;
        }

        .exceptionAddForm {
            flex-direction: column;
            align-items: stretch;
        }
   }
</style>

<script setup>
import { ref, onMounted, computed } from 'vue';
import axios from 'axios';
import { authHeader } from '../helpers/authHeader.js';
import { getDate, getTime } from '../dateUtils.js';
const activeTab = ref('periods');

const weekDays = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday'
];

// Planning periods state
const planningLoading = ref(false);
const planningUpdating = ref(false);
const planningPeriods = ref([]);
const planningEditVisible = ref(false);
const planningDialogMode = ref('edit');
const editingPlanningId = ref(null);
const planningDeletingId = ref(null);
const editingBeginDate = ref(null);
const editingEndDate = ref(null);
const weekScheduleRows = ref(defaultWeekRows());
const readonlyRecurrences = ref([]);

// Exception days state
const exceptionsLoading = ref(false);
const exceptions = ref([]);
const newExceptionDate = ref(null);
const newExceptionEndDate = ref(null);
const newExceptionLabel = ref('');

onMounted(() => {
    loadPlanningPeriods();
    loadExceptions();
});

function tabButtonClass(tab) {
    return activeTab.value === tab ? 'planningTabButtonActive' : 'planningTabButton';
}

// ---- Planning periods ----

function loadPlanningPeriods() {
    planningLoading.value = true;

    axios
      .get(`${process.env.VUE_APP_API_URL}/Plannings`, { headers: authHeader() })
      .then(response => {
          planningLoading.value = false;
          planningPeriods.value = Array.isArray(response.data) ? response.data : [];
      })
      .catch(reason => {
          planningLoading.value = false;
          console.log(reason);
          alert("error");
      });
}

function dayLabel(dayOfWeek) {
    const labels = {
        Monday: 'Lundi',
        Tuesday: 'Mardi',
        Wednesday: 'Mercredi',
        Thursday: 'Jeudi',
        Friday: 'Vendredi',
        Saturday: 'Samedi',
        Sunday: 'Dimanche'
    };
    return labels[dayOfWeek] || dayOfWeek;
}

const dayScheduleRows = computed(() => weekDays.map((day) => {
    const weekA = weekScheduleRows.value.find(r => r.dayOfWeek === day && r.weekType === 'A');
    const weekB = weekScheduleRows.value.find(r => r.dayOfWeek === day && r.weekType === 'B');
    return { dayOfWeek: day, weekA, weekB };
}));

function templateRows({ openWeekDays = [], copyAtoB = false } = {}) {
    const rows = [];
    weekDays.forEach((day) => {
        ['A', 'B'].forEach((weekType) => {
            const enabled = copyAtoB
                ? openWeekDays.includes(day)
                : (weekType === 'A' && openWeekDays.includes(day));
            rows.push({
                index: rows.length,
                dayOfWeek: day,
                weekType,
                intervalWeeks: 2,
                enabled,
                startTime: '08:00',
                endTime: '18:00'
            });
        });
    });
    return rows;
}

function defaultWeekRows() {
    return templateRows();
}

function defaultNewWeekRows() {
    return templateRows({
        openWeekDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        copyAtoB: true
    });
}

function parseDate(value) {
    if (!value) {
        return null;
    }
    const parsed = new Date(`${value}T00:00:00`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function parseRecurrenceItems(json) {
    try {
        const parsed = JSON.parse(json || "[]");
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

function buildWeekRowsForWeek(period, weekType) {
    const items = parseRecurrenceItems(period.recurrenceItemsJson || "[]")
        .filter(i => i.type === "weekly");

    return weekDays.map((day) => {
        const exact = items.find(i =>
            i.dayOfWeek === day &&
            Number(i.intervalWeeks || 1) === 2 &&
            (i.weekType === 'B' ? 'B' : 'A') === weekType
        );

        if (exact) {
            return {
                day,
                label: dayLabel(day),
                value: `${exact.startTime}-${exact.endTime}`
            };
        }

        const weekly = items.find(i => i.dayOfWeek === day && Number(i.intervalWeeks || 1) === 1);
        return {
            day,
            label: dayLabel(day),
            value: weekly ? `${weekly.startTime}-${weekly.endTime}` : 'Fermé'
        };
    });
}

function openPlanningEdit(period) {
    planningDialogMode.value = 'edit';
    editingPlanningId.value = period.planningId;
    editingBeginDate.value = parseDate(period.begin);
    editingEndDate.value = parseDate(period.end);
    const items = parseRecurrenceItems(period.recurrenceItemsJson);
    const rows = defaultWeekRows();
    items
        .filter(i => i.editable === "true" && i.type === "weekly")
        .forEach(i => {
            const intervalWeeks = Number(i.intervalWeeks || 1);
            if (intervalWeeks === 1) {
                rows
                    .filter(r => r.dayOfWeek === i.dayOfWeek)
                    .forEach((row) => {
                        row.enabled = true;
                        row.startTime = i.startTime || row.startTime;
                        row.endTime = i.endTime || row.endTime;
                        row.intervalWeeks = 2;
                    });
                return;
            }

            const targetWeekType = i.weekType === 'B' ? 'B' : 'A';
            const row = rows.find(r => r.dayOfWeek === i.dayOfWeek && r.weekType === targetWeekType);
            if (!row) {
                return;
            }

            row.enabled = true;
            row.startTime = i.startTime || row.startTime;
            row.endTime = i.endTime || row.endTime;
            row.intervalWeeks = 2;
        });
    weekScheduleRows.value = rows;
    readonlyRecurrences.value = items
        .filter(i => i.editable !== "true")
        .map(i => ({
            index: Number(i.index),
            date: i.date || "",
            startTime: i.startTime || "",
            endTime: i.endTime || ""
        }));
    planningEditVisible.value = true;
}

function openNewPlanning() {
    planningDialogMode.value = 'create';
    editingPlanningId.value = null;
    editingBeginDate.value = new Date();
    editingEndDate.value = null;
    weekScheduleRows.value = defaultNewWeekRows();
    readonlyRecurrences.value = [];
    planningEditVisible.value = true;
}

function isValidTime(value) {
    return /^([01][0-9]|2[0-3]):[0-5][0-9]$/.test(value || "");
}

function savePlanningPeriod() {
    if (planningUpdating.value) {
        return;
    }

    if (!editingBeginDate.value) {
        alert("Période invalide.");
        return;
    }
    const enabledRows = weekScheduleRows.value.filter(r => r.enabled);
    if (enabledRows.length === 0) {
        alert("Merci de laisser au moins un jour ouvert.");
        return;
    }

    for (const r of enabledRows) {
        if (!isValidTime(r.startTime) || !isValidTime(r.endTime) || r.endTime <= r.startTime) {
            alert("Merci de vérifier les horaires de récurrence (HH:mm).");
            return;
        }
    }

    const payload = {
        Begin: getDate(editingBeginDate.value),
        End: editingEndDate.value ? getDate(editingEndDate.value) : "",
        Recurrences: weekScheduleRows.value.map((r) => ({
            Index: r.index,
            Enabled: !!r.enabled,
            DayOfWeek: r.dayOfWeek,
            WeekType: r.weekType,
            IntervalWeeks: 2,
            StartTime: r.startTime,
            EndTime: r.endTime
        }))
    };

    var options = {
      "headers" :  authHeader(true)
    };

    planningUpdating.value = true;
    if (planningDialogMode.value === 'create') {
        createPlanningPeriod(payload, options);
        return;
    }
    if (!editingPlanningId.value) {
        planningUpdating.value = false;
        alert("Période introuvable.");
        return;
    }

    axios
      .post(`${process.env.VUE_APP_API_URL}/Plannings/update?planningId=${editingPlanningId.value}`, JSON.stringify(payload), options)
      .then(() => {
          planningUpdating.value = false;
          planningEditVisible.value = false;
          loadPlanningPeriods();
      })
      .catch(reason => {
          planningUpdating.value = false;
          console.log(reason);
          alert("Erreur lors de la sauvegarde de la période.");
      });
}

function dayToJsIndex(day) {
    return {
        Sunday: 0,
        Monday: 1,
        Tuesday: 2,
        Wednesday: 3,
        Thursday: 4,
        Friday: 5,
        Saturday: 6
    }[day];
}

function buildDateForDayName(baseDate, dayName, hhmm) {
    const [hh, mm] = (hhmm || "00:00").split(':').map(x => Number(x));
    const d = new Date(baseDate);
    d.setHours(0, 0, 0, 0);
    const target = dayToJsIndex(dayName);
    const dayOffset = (target - d.getDay() + 7) % 7;
    d.setDate(d.getDate() + dayOffset);
    d.setHours(hh, mm, 0, 0);
    return d;
}

async function createPlanningPeriod(payload, options) {
    const enabled = payload.Recurrences.filter(r => r.Enabled);
    const effectiveFrom = payload.Begin;
    const uniqueEnabled = [];
    const seen = new Set();

    enabled.forEach((r) => {
        const key = `${r.DayOfWeek}-${r.WeekType}-${r.StartTime}-${r.EndTime}`;
        if (!seen.has(key)) {
            seen.add(key);
            uniqueEnabled.push(r);
        }
    });

    try {
        for (const r of uniqueEnabled) {
            const start = buildDateForDayName(editingBeginDate.value, r.DayOfWeek, r.StartTime);
            const end = buildDateForDayName(editingBeginDate.value, r.DayOfWeek, r.EndTime);
            const formattedStart = getDate(start) + getTime(start.toTimeString().substring(0,5));
            const formattedEnd = getDate(end) + getTime(end.toTimeString().substring(0,5));
            const weekOffsetWeeks = r.WeekType === 'B' ? 1 : 0;

            await axios.post(
                `${process.env.VUE_APP_API_URL}/Plannings?start=${formattedStart}&end=${formattedEnd}&effectiveFrom=${effectiveFrom}&intervalWeeks=2&weekOffsetWeeks=${weekOffsetWeeks}`,
                JSON.stringify({}),
                options
            );
        }

        planningUpdating.value = false;
        planningEditVisible.value = false;
        loadPlanningPeriods();
    } catch (reason) {
        planningUpdating.value = false;
        console.log(reason);
        alert("Erreur lors de la création de la période.");
    }
}

function deletePlanningPeriod(period) {
    if (!period || !period.planningId) {
        return;
    }

    const confirmDelete = window.confirm(`Supprimer la période du ${period.begin}${period.end ? ` au ${period.end}` : ''} ?`);
    if (!confirmDelete) {
        return;
    }

    planningDeletingId.value = period.planningId;

    axios
      .delete(`${process.env.VUE_APP_API_URL}/Plannings?planningId=${period.planningId}`, { headers: authHeader() })
      .then(() => {
          planningDeletingId.value = null;
          loadPlanningPeriods();
      })
      .catch((reason) => {
          planningDeletingId.value = null;
          console.log(reason);
          alert("Erreur lors de la suppression de la période.");
      });
}

// ---- Exception days ----

function loadExceptions() {
    exceptionsLoading.value = true;

    axios
      .get(`${process.env.VUE_APP_API_URL}/PlanningExceptions`, { headers: authHeader() })
      .then(response => {
          exceptionsLoading.value = false;
          exceptions.value = response.data;
      })
      .catch(reason => {
          exceptionsLoading.value = false;
          console.log(reason);
          alert("Erreur lors du chargement des jours d'exception.");
      });
}

function formatExceptionDate(dateStr) {
    if (!dateStr) {
        return '';
    }
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) {
        return dateStr;
    }
    const dayNames = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
    const monthNames = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
    return `${dayNames[d.getDay()]} ${d.getDate()} ${monthNames[d.getMonth()]} ${d.getFullYear()}`;
}

function addException() {
    if (!newExceptionDate.value) {
        alert("Veuillez sélectionner une date.");
        return;
    }

    exceptionsLoading.value = true;

    const payload = {
        ExceptionDate: getDate(newExceptionDate.value),
        Label: newExceptionLabel.value.trim()
    };

    const hasEndDate = newExceptionEndDate.value && newExceptionEndDate.value >= newExceptionDate.value;

    const url = hasEndDate
      ? `${process.env.VUE_APP_API_URL}/PlanningExceptions/range?to=${getDate(newExceptionEndDate.value)}`
      : `${process.env.VUE_APP_API_URL}/PlanningExceptions`;

    axios
      .post(url, payload, { headers: authHeader(true) })
      .then(() => {
          newExceptionDate.value = null;
          newExceptionEndDate.value = null;
          newExceptionLabel.value = '';
          loadExceptions();
      })
      .catch(reason => {
          exceptionsLoading.value = false;
          console.log(reason);
          if (reason.response?.status === 400) {
              alert("Ces dates existent déjà dans les exceptions.");
          } else {
              alert("Erreur lors de l'ajout du jour d'exception.");
          }
      });
}

function deleteException(ex) {
    const confirmDelete = window.confirm(`Supprimer le jour d'exception du ${formatExceptionDate(ex.exceptionDate)} ?`);
    if (!confirmDelete) {
        return;
    }

    axios
      .delete(`${process.env.VUE_APP_API_URL}/PlanningExceptions?id=${ex.planningExceptionId}`, { headers: authHeader() })
      .then(() => {
          loadExceptions();
      })
      .catch(reason => {
          console.log(reason);
          alert("Erreur lors de la suppression du jour d'exception.");
      });
}

</script>
